import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';

import { formatAudioTime } from '@/components/media/audio/audioClipUtils.js';
import {
  getJianyingVideoContentType,
  normalizeJianyingVideoSelection,
} from '@/features/automation/jianying/utils/jianyingVideoUpload.js';
import JianyingSlotGroupTree from '../JianyingSlotGroupTree.js';

const VIDEO_ACCEPT = '.mp4,.mov,video/mp4,video/quicktime';

const readVideoDuration = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  const finish = () => URL.revokeObjectURL(url);
  video.preload = 'metadata';
  video.onloadedmetadata = () => {
    const duration = video.duration;
    finish();
    if (Number.isFinite(duration) && duration > 0) resolve(duration);
    else reject(new Error('无法读取视频时长'));
  };
  video.onerror = () => {
    finish();
    reject(new Error('无法读取视频文件'));
  };
  video.src = url;
});

const JianyingVideoSlotEditor = ({
  disabled,
  error,
  onChange,
  onError,
  slot,
  value,
}) => {
  const inputRef = useRef(null);
  const [reading, setReading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const fileDurationSeconds = Number(value?.file_duration_us || 0) / 1_000_000;
  const startSeconds = Number(value?.source_start_us || 0) / 1_000_000;
  const endSeconds = startSeconds
    + Number(value?.source_duration_us || 0) / 1_000_000;
  const targetDurationUs = Number(slot.config?.target_duration_us || 0);
  const targetDurationSeconds = targetDurationUs / 1_000_000;
  const speed = Number(value?.source_duration_us || 0) / targetDurationUs;

  useEffect(() => {
    if (!value?.file) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(value.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value?.file]);

  const emitSelection = (file, duration, start, end) => {
    const selection = normalizeJianyingVideoSelection({
      fileDurationSeconds: duration,
      startSeconds: start,
      endSeconds: end,
      targetDurationUs,
    });
    onChange({
      kind: 'video',
      file,
      file_name: file.name,
      ...selection,
    });
  };

  const selectFile = async (file) => {
    if (!file) return;
    setReading(true);
    try {
      getJianyingVideoContentType(file);
      const duration = await readVideoDuration(file);
      emitSelection(file, duration, 0, duration);
    } catch (selectionError) {
      onError?.(selectionError);
    } finally {
      setReading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const updateRange = (nextStart, nextEnd) => {
    try {
      emitSelection(value.file, fileDurationSeconds, nextStart, nextEnd);
    } catch (selectionError) {
      onError?.(selectionError);
    }
  };

  return (
    <article className={`jianying-video-slot ${error ? 'is-invalid' : ''}`.trim()}>
      <div className="jianying-video-slot__heading">
        <div>
          <strong>{slot.description || slot.key}</strong>
          <span>模板成片 {formatAudioTime(targetDurationSeconds)}</span>
        </div>
        {Number.isFinite(speed) && speed > 0 ? (
          <span className="jianying-video-slot__speed">{speed.toFixed(2)}x</span>
        ) : null}
      </div>

      {previewUrl ? (
        <video
          className="jianying-video-slot__preview"
          src={previewUrl}
          controls
          preload="metadata"
        />
      ) : (
        <div className="jianying-video-slot__empty">尚未选择视频</div>
      )}

      {value?.file_name ? (
        <>
          <div className="jianying-video-slot__file" title={value.file_name}>
            <i className="bi bi-camera-video" aria-hidden="true" />
            <span>{value.file_name}</span>
          </div>
          <div className="jianying-video-slot__range">
            <label>
              <span>开始时间</span>
              <input
                type="number"
                min="0"
                max={Math.max(0, endSeconds - 0.01)}
                step="0.1"
                value={startSeconds}
                disabled={disabled}
                onChange={(event) => updateRange(
                  Number(event.target.value),
                  endSeconds,
                )}
              />
            </label>
            <label>
              <span>结束时间</span>
              <input
                type="number"
                min={startSeconds + 0.01}
                max={fileDurationSeconds}
                step="0.1"
                value={endSeconds}
                disabled={disabled}
                onChange={(event) => updateRange(
                  startSeconds,
                  Number(event.target.value),
                )}
              />
            </label>
          </div>
          <div className="jianying-video-slot__metrics">
            <span>原视频 {formatAudioTime(fileDurationSeconds)}</span>
            <span>已选 {formatAudioTime(endSeconds - startSeconds)}</span>
          </div>
          <p className="jianying-video-slot__notice">
            会根据选择的视频修改倍速值
          </p>
        </>
      ) : null}

      <div className="jianying-video-slot__actions">
        <input
          ref={inputRef}
          type="file"
          accept={VIDEO_ACCEPT}
          hidden
          disabled={disabled || reading}
          onChange={(event) => selectFile(event.target.files?.[0])}
        />
        <Button
          type="button"
          size="sm"
          variant="outline-primary"
          disabled={disabled || reading}
          onClick={() => inputRef.current?.click()}
        >
          {reading ? <Spinner size="sm" /> : <i className="bi bi-folder2-open" />}
          <span>{value?.file_name ? '更换视频' : '选择视频'}</span>
        </Button>
        {value?.file_name ? (
          <Button
            type="button"
            size="sm"
            variant="outline-danger"
            title="移除视频"
            disabled={disabled}
            onClick={() => onChange(undefined)}
          >
            <i className="bi bi-trash3" />
          </Button>
        ) : null}
      </div>
      {error ? <div className="jianying-video-slot__error">{error}</div> : null}
    </article>
  );
};

const JianyingVideoMaterialPanel = ({
  disabled = false,
  errors = {},
  onChange,
  onError,
  sections = [],
  slots = [],
  values = {},
}) => {
  if (!slots.length) return null;
  return (
    <section className="jianying-material-panel jianying-video-material-panel">
      <div className="jianying-material-panel__header">
        <span>
          <i className="bi bi-camera-video" aria-hidden="true" />
          <strong>视频素材</strong>
          <small>{slots.length} 项</small>
        </span>
      </div>
      <div className="jianying-video-material-panel__content">
        <JianyingSlotGroupTree
          sections={sections}
          renderSlot={(slot) => (
          <JianyingVideoSlotEditor
            key={slot.key}
            disabled={disabled}
            error={errors[slot.key]}
            onChange={(nextValue) => onChange(slot.key, nextValue)}
            onError={onError}
            slot={slot}
            value={values[slot.key]}
          />
          )}
        />
      </div>
    </section>
  );
};

export default JianyingVideoMaterialPanel;
